/* ============================================
   MAGGD — Product Page JS
   ============================================ */

document.addEventListener("DOMContentLoaded", () => {
  // ==========================================
  //  3D INTERACTIVE VIEWER
  // ==========================================
  const viewer = document.getElementById("viewer3D");
  const inner = document.getElementById("viewer3DInner");
  const lightOverlay = document.getElementById("viewer3DLight");
  const hint = document.getElementById("viewer3DHint");
  const badge = document.getElementById("viewer3DBadge");
  const resetBtn = document.getElementById("viewer3DReset");
  const zoomInBtn = document.getElementById("viewer3DZoomIn");
  const zoomOutBtn = document.getElementById("viewer3DZoomOut");

  // 3D viewer state (accessible to thumbnail handler)
  let rotateX = 0,
    rotateY = 0;
  let zoom = 1;
  let velocityX = 0,
    velocityY = 0;
  let animFrame = null;

  if (viewer && inner) {
    let isDragging = false;
    let startX = 0,
      startY = 0;
    let targetRotateX = 0,
      targetRotateY = 0;
    let lastX = 0,
      lastY = 0;
    let lastTime = 0;
    let hasInteracted = false;
    let isIdle = true;

    const MAX_ROTATE = 25; // Max rotation degrees
    const SENSITIVITY = 0.4; // Drag sensitivity
    const FRICTION = 0.92; // Inertia friction (lower = more friction)
    const MIN_ZOOM = 1;
    const MAX_ZOOM = 2.5;
    const ZOOM_STEP = 0.15;

    // Start with idle animation
    inner.classList.add("idle");

    // --- Apply Transform ---
    function applyTransform() {
      inner.style.transform = `
        rotateY(${rotateY}deg)
        rotateX(${rotateX}deg)
        scale(${zoom})
      `;

      // Dynamic lighting based on rotation
      const lightX = 50 + rotateY * 1.5;
      const lightY = 50 - rotateX * 1.5;
      const intensity = Math.abs(rotateY) + Math.abs(rotateX);
      const lightOpacity = Math.min(intensity * 0.012, 0.25);
      lightOverlay.style.background = `
        radial-gradient(ellipse at ${lightX}% ${lightY}%,
          rgba(255,255,255,${lightOpacity}) 0%,
          rgba(0,0,0,${lightOpacity * 0.4}) 100%)
      `;
    }

    // --- Inertia Animation Loop ---
    function animate() {
      if (!isDragging) {
        velocityX *= FRICTION;
        velocityY *= FRICTION;

        if (Math.abs(velocityX) > 0.05 || Math.abs(velocityY) > 0.05) {
          rotateY = clamp(rotateY + velocityX, -MAX_ROTATE, MAX_ROTATE);
          rotateX = clamp(rotateX - velocityY, -MAX_ROTATE, MAX_ROTATE);
          applyTransform();
          animFrame = requestAnimationFrame(animate);
        } else {
          velocityX = 0;
          velocityY = 0;
        }
      }
    }

    function clamp(val, min, max) {
      return Math.max(min, Math.min(max, val));
    }

    // --- Pointer Down ---
    function onPointerDown(e) {
      if (e.target.closest(".viewer-3d-btn")) return;
      isDragging = true;
      startX = e.clientX || e.touches?.[0]?.clientX || 0;
      startY = e.clientY || e.touches?.[0]?.clientY || 0;
      lastX = startX;
      lastY = startY;
      lastTime = Date.now();
      velocityX = 0;
      velocityY = 0;

      // Stop idle animation on first interaction
      if (isIdle) {
        isIdle = false;
        inner.classList.remove("idle");
      }

      // Hide hint
      if (!hasInteracted && hint) {
        hasInteracted = true;
        hint.classList.add("hidden");
        setTimeout(() => (hint.style.display = "none"), 600);
      }

      inner.style.transition = "none";
      if (animFrame) cancelAnimationFrame(animFrame);
    }

    // --- Pointer Move ---
    function onPointerMove(e) {
      if (!isDragging) return;
      e.preventDefault();

      const clientX = e.clientX || e.touches?.[0]?.clientX || 0;
      const clientY = e.clientY || e.touches?.[0]?.clientY || 0;
      const now = Date.now();
      const dt = Math.max(now - lastTime, 1);

      const deltaX = clientX - lastX;
      const deltaY = clientY - lastY;

      // Track velocity for inertia
      velocityX = (deltaX / dt) * 16;
      velocityY = (deltaY / dt) * 16;

      rotateY = clamp(rotateY + deltaX * SENSITIVITY, -MAX_ROTATE, MAX_ROTATE);
      rotateX = clamp(rotateX - deltaY * SENSITIVITY, -MAX_ROTATE, MAX_ROTATE);

      lastX = clientX;
      lastY = clientY;
      lastTime = now;

      applyTransform();
    }

    // --- Pointer Up ---
    function onPointerUp() {
      if (!isDragging) return;
      isDragging = false;
      inner.style.transition = "transform 0.08s ease-out";

      // Kick off inertia
      if (Math.abs(velocityX) > 0.1 || Math.abs(velocityY) > 0.1) {
        animFrame = requestAnimationFrame(animate);
      }
    }

    // --- Mouse Events ---
    viewer.addEventListener("mousedown", onPointerDown);
    window.addEventListener("mousemove", onPointerMove);
    window.addEventListener("mouseup", onPointerUp);

    // --- Touch Events ---
    viewer.addEventListener(
      "touchstart",
      (e) => {
        if (e.touches.length === 1) {
          onPointerDown(e.touches[0]);
        }
      },
      { passive: false },
    );

    viewer.addEventListener(
      "touchmove",
      (e) => {
        if (e.touches.length === 1) {
          e.preventDefault();
          onPointerMove(e.touches[0]);
        } else if (e.touches.length === 2) {
          // Pinch to zoom
          e.preventDefault();
          handlePinch(e);
        }
      },
      { passive: false },
    );

    viewer.addEventListener("touchend", onPointerUp);

    // --- Pinch Zoom ---
    let lastPinchDist = 0;

    function handlePinch(e) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (lastPinchDist > 0) {
        const delta = dist - lastPinchDist;
        zoom = clamp(zoom + delta * 0.005, MIN_ZOOM, MAX_ZOOM);
        applyTransform();
      }
      lastPinchDist = dist;
    }

    viewer.addEventListener("touchend", () => {
      lastPinchDist = 0;
    });

    // --- Scroll Zoom (Desktop) ---
    viewer.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
        zoom = clamp(zoom + delta, MIN_ZOOM, MAX_ZOOM);

        if (!hasInteracted) {
          hasInteracted = true;
          if (hint) {
            hint.classList.add("hidden");
            setTimeout(() => (hint.style.display = "none"), 600);
          }
        }
        if (isIdle) {
          isIdle = false;
          inner.classList.remove("idle");
        }

        applyTransform();
      },
      { passive: false },
    );

    // --- Zoom Buttons ---
    if (zoomInBtn) {
      zoomInBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        zoom = clamp(zoom + ZOOM_STEP, MIN_ZOOM, MAX_ZOOM);
        inner.style.transition =
          "transform 0.4s cubic-bezier(0.23, 1, 0.32, 1)";
        applyTransform();
      });
    }

    if (zoomOutBtn) {
      zoomOutBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        zoom = clamp(zoom - ZOOM_STEP, MIN_ZOOM, MAX_ZOOM);
        inner.style.transition =
          "transform 0.4s cubic-bezier(0.23, 1, 0.32, 1)";
        applyTransform();
      });
    }

    // --- Reset Button ---
    if (resetBtn) {
      resetBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        rotateX = 0;
        rotateY = 0;
        zoom = 1;
        velocityX = 0;
        velocityY = 0;
        inner.classList.add("resetting");
        applyTransform();
        setTimeout(() => {
          inner.classList.remove("resetting");
          inner.classList.add("idle");
          isIdle = true;
        }, 600);
      });
    }

    // --- Auto-hide hint after 5 seconds ---
    setTimeout(() => {
      if (!hasInteracted && hint) {
        hint.classList.add("hidden");
        setTimeout(() => (hint.style.display = "none"), 600);
      }
    }, 5000);
  }

  // ==========================================
  //  GALLERY THUMBNAILS
  // ==========================================
  const mainImage = document.getElementById("mainImage");
  const thumbs = document.querySelectorAll(".gallery-thumb");

  const heroSrc = mainImage ? mainImage.src : ""; // Original hero image source

  thumbs.forEach((thumb, index) => {
    thumb.addEventListener("click", () => {
      const newSrc = thumb.dataset.img;
      const isHeroImage = index === 0; // First thumbnail = hero image with 3D

      // Simple fade swap
      mainImage.style.transition = "opacity 0.3s ease";
      mainImage.style.opacity = "0";
      setTimeout(() => {
        // Keep the main image responsive on swap: set srcset (cleared for the
        // placeholder thumbs that have no data-srcset) before swapping src.
        mainImage.srcset = thumb.dataset.srcset || "";
        mainImage.src = newSrc;
        mainImage.style.opacity = "1";
      }, 300);

      // Toggle 3D viewer on/off based on which thumbnail is selected
      if (viewer && inner) {
        if (isHeroImage) {
          // Re-enable 3D
          viewer.style.pointerEvents = "";
          viewer.style.cursor = "grab";
          inner.classList.add("idle");
          inner.style.transform = "";
          // Show 3D UI elements
          document
            .querySelectorAll(
              ".viewer-3d-controls, .viewer-3d-badge, .viewer-3d-hint",
            )
            .forEach((el) => {
              el.style.display = "";
            });
          if (lightOverlay) lightOverlay.style.display = "";
        } else {
          // Disable 3D — make it a normal static image
          viewer.style.pointerEvents = "none";
          viewer.style.cursor = "default";
          inner.classList.remove("idle");
          inner.style.transition = "transform 0.4s ease";
          inner.style.transform = "none";
          rotateX = 0;
          rotateY = 0;
          zoom = 1;
          velocityX = 0;
          velocityY = 0;
          if (animFrame) cancelAnimationFrame(animFrame);
          // Hide 3D UI elements
          document
            .querySelectorAll(
              ".viewer-3d-controls, .viewer-3d-badge, .viewer-3d-hint",
            )
            .forEach((el) => {
              el.style.display = "none";
            });
          if (lightOverlay) {
            lightOverlay.style.display = "none";
            lightOverlay.style.background = "none";
          }
        }
      }

      thumbs.forEach((t) => t.classList.remove("active"));
      thumb.classList.add("active");
    });
  });

  // ==========================================
  //  CLICK-TO-ZOOM (main image)
  // ==========================================
  const galleryMain = document.getElementById("galleryMain");
  if (galleryMain && mainImage) {
    const ZOOM = 2.2;
    let zoomed = false;
    let tx = 0; // current horizontal pan offset (px)
    let ty = 0; // current vertical pan offset (px)
    let maxX = 0; // max pan distance each side (px)
    let maxY = 0;
    // Touch-drag references (captured at touchstart).
    let dragStartX = 0;
    let dragStartY = 0;
    let dragTX = 0;
    let dragTY = 0;

    function clamp(v, m) {
      return Math.max(-m, Math.min(m, v));
    }

    // Recompute pan bounds from the (untransformed) container box.
    function refreshBounds() {
      const rect = galleryMain.getBoundingClientRect();
      maxX = (rect.width * (ZOOM - 1)) / 2;
      maxY = (rect.height * (ZOOM - 1)) / 2;
      return rect;
    }

    function apply() {
      mainImage.style.transform =
        "translate(" + tx + "px, " + ty + "px) scale(" + ZOOM + ")";
    }

    // Desktop: map cursor position across the frame to a pan offset, so moving
    // toward an edge reveals that edge. Continuous from the zoom-in point, so
    // there's no jump.
    function panFromPointer(e) {
      const rect = refreshBounds();
      const px = clamp((e.clientX - rect.left) / rect.width - 0.5, 0.5);
      const py = clamp((e.clientY - rect.top) / rect.height - 0.5, 0.5);
      tx = clamp(-px * 2 * maxX, maxX);
      ty = clamp(-py * 2 * maxY, maxY);
      apply();
    }

    function zoomIn(e) {
      refreshBounds();
      mainImage.style.transformOrigin = "center center";
      mainImage.style.transition = "transform 0.25s ease";
      if (e && typeof e.clientX === "number") {
        panFromPointer(e); // zoom toward where they clicked / tapped
      } else {
        tx = 0;
        ty = 0;
        apply();
      }
      galleryMain.classList.add("is-zoomed");
      // Dismiss the "Tap to zoom" hint for good once they've used the zoom.
      galleryMain.classList.add("zoom-used");
      zoomed = true;
      // Drop the transition after the zoom-in animation so panning tracks 1:1
      // with the finger/cursor instead of lagging behind it.
      setTimeout(() => {
        if (zoomed) mainImage.style.transition = "none";
      }, 250);
    }

    function zoomOut() {
      mainImage.style.transition = "transform 0.25s ease";
      mainImage.style.transform = "";
      mainImage.style.transformOrigin = "";
      tx = 0;
      ty = 0;
      galleryMain.classList.remove("is-zoomed");
      zoomed = false;
    }

    galleryMain.addEventListener("click", (e) => {
      if (zoomed) zoomOut();
      else zoomIn(e);
    });

    galleryMain.addEventListener("mousemove", (e) => {
      if (zoomed) panFromPointer(e);
    });

    // Touch: grab-and-drag. The image follows the finger 1:1 (same direction),
    // relative to where the drag started — no jump, no over-sensitivity.
    galleryMain.addEventListener(
      "touchstart",
      (e) => {
        if (!zoomed || !e.touches[0]) return;
        refreshBounds();
        dragStartX = e.touches[0].clientX;
        dragStartY = e.touches[0].clientY;
        dragTX = tx;
        dragTY = ty;
      },
      { passive: true },
    );

    galleryMain.addEventListener(
      "touchmove",
      (e) => {
        if (!zoomed || !e.touches[0]) return;
        e.preventDefault(); // stop the page scrolling while panning
        tx = clamp(dragTX + (e.touches[0].clientX - dragStartX), maxX);
        ty = clamp(dragTY + (e.touches[0].clientY - dragStartY), maxY);
        apply();
      },
      { passive: false },
    );

    // Switching thumbnails resets the zoom so the new image starts un-zoomed.
    thumbs.forEach((t) =>
      t.addEventListener("click", () => {
        if (zoomed) zoomOut();
      }),
    );
  }

  // ==========================================
  //  THUMBNAIL CAROUSEL ARROWS
  // ==========================================
  const thumbsTrack = document.getElementById("galleryThumbs");
  const thumbPrev = document.getElementById("thumbPrev");
  const thumbNext = document.getElementById("thumbNext");
  if (thumbsTrack && thumbPrev && thumbNext) {
    const updateArrows = () => {
      const maxScroll = thumbsTrack.scrollWidth - thumbsTrack.clientWidth;
      const overflowing = maxScroll > 2;
      thumbPrev.hidden = !overflowing || thumbsTrack.scrollLeft <= 1;
      thumbNext.hidden =
        !overflowing || thumbsTrack.scrollLeft >= maxScroll - 1;
    };
    const step = () => Math.max(thumbsTrack.clientWidth * 0.7, 140);
    thumbPrev.addEventListener("click", () =>
      thumbsTrack.scrollBy({ left: -step(), behavior: "smooth" }),
    );
    thumbNext.addEventListener("click", () =>
      thumbsTrack.scrollBy({ left: step(), behavior: "smooth" }),
    );
    thumbsTrack.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    if (window.ResizeObserver) {
      new ResizeObserver(updateArrows).observe(thumbsTrack);
    }
    updateArrows();
  }

  // ==========================================
  //  VARIANT SELECTION + PRICING (real Shopify data)
  // ==========================================
  const productForm = document.querySelector("[data-product-form]");
  const productJsonEl = document.querySelector("[data-product-json]");
  let productData = null;
  if (productJsonEl) {
    try {
      productData = JSON.parse(productJsonEl.textContent);
    } catch (e) {
      productData = null;
    }
  }

  const variantIdInput = productForm
    ? productForm.querySelector("[data-variant-id]")
    : null;
  const priceEl = document.querySelector("[data-product-price]");
  const compareEl = document.querySelector("[data-product-compare]");
  const addBtn = document.getElementById("addToCart");
  const addText = document.querySelector("[data-add-to-cart-text]");
  const ADD_HTML = addText ? addText.innerHTML : "";
  const SOLD_OUT =
    (window.maggdTranslations && window.maggdTranslations.sold_out) ||
    "Sold Out";

  function maggdFormatMoney(cents) {
    if (typeof cents !== "number") cents = parseInt(cents, 10);
    if (isNaN(cents)) return "";
    const fmt = window.shopCurrency && window.shopCurrency.money_format;
    const dec = (cents / 100).toFixed(2);
    const amount =
      dec.split(".")[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
      "." +
      dec.split(".")[1]; // 1,299.00
    const noDec = String(Math.round(cents / 100)).replace(
      /\B(?=(\d{3})+(?!\d))/g,
      ",",
    ); // 1,299
    // No store format available -> show the amount WITHOUT guessing a symbol
    // (never fall back to "$", which is wrong for non-USD stores like EGP).
    if (!fmt) return null;
    return fmt
      .replace("{{amount}}", amount)
      .replace("{{amount_no_decimals}}", noDec)
      .replace("{{amount_with_comma_separator}}", dec.replace(".", ","))
      .replace("{{amount_no_decimals_with_comma_separator}}", noDec);
  }

  function getQuantity() {
    const el = document.getElementById("qtyValue");
    const q = parseInt(el ? el.value : "1", 10);
    return isNaN(q) || q < 1 ? 1 : q;
  }

  function activateInGroup(el, groupSelector) {
    document.querySelectorAll(groupSelector).forEach((s) => {
      s.classList.remove("active");
      if (s.hasAttribute("aria-checked"))
        s.setAttribute("aria-checked", "false");
    });
    el.classList.add("active");
    if (el.hasAttribute("aria-checked"))
      el.setAttribute("aria-checked", "true");
  }

  function getSelectedOptions() {
    if (!productData) return [];
    const out = [];
    for (let i = 1; i <= productData.options.length; i++) {
      let val = null;
      const activeBtn = document.querySelector(
        '.size-btn.active[data-option-position="' +
          i +
          '"], .color-swatch.active[data-option-position="' +
          i +
          '"]',
      );
      if (activeBtn) val = activeBtn.dataset.optionValue;
      if (val == null) {
        const sel = document.querySelector(
          'select.product-select[data-option-position="' + i + '"]',
        );
        if (sel) val = sel.value;
      }
      out.push(val);
    }
    return out;
  }

  function findVariant(selected) {
    if (!productData) return null;
    return (
      productData.variants.find((v) => {
        const vo = v.options || [v.option1, v.option2, v.option3];
        return selected.every((val, idx) => val == null || vo[idx] === val);
      }) || null
    );
  }

  function setAddButtonState(available, totalCents) {
    if (addBtn) addBtn.disabled = !available;
    if (!addText) return;
    if (available) {
      // Restore the original markup (label + price span) if a sold-out state replaced it.
      if (!addText.querySelector("[data-cart-price]") && ADD_HTML)
        addText.innerHTML = ADD_HTML;
      const cp = addText.querySelector("[data-cart-price]");
      const ts = maggdFormatMoney(totalCents);
      if (cp && ts != null) cp.textContent = ts;
    } else {
      addText.textContent = SOLD_OUT;
    }
  }

  // Keep the variant id, price, and Add-to-Cart state in sync with the current selection.
  function syncVariant() {
    if (!productData) return;
    const selected = getSelectedOptions();
    const variant = findVariant(selected);

    selected.forEach((val, idx) => {
      const lbl = document.querySelector(
        '[data-option-value="' + (idx + 1) + '"]',
      );
      if (lbl && val != null) lbl.textContent = val;
    });

    if (!variant) {
      setAddButtonState(false);
      return;
    }

    if (variantIdInput) variantIdInput.value = variant.id;

    const qty = getQuantity();
    if (priceEl) {
      const ps = maggdFormatMoney(variant.price);
      if (ps != null) priceEl.textContent = ps;
    }
    if (compareEl) {
      if (
        variant.compare_at_price &&
        variant.compare_at_price > variant.price
      ) {
        const cs = maggdFormatMoney(variant.compare_at_price);
        if (cs != null) compareEl.textContent = cs;
        compareEl.style.display = "";
      } else {
        compareEl.style.display = "none";
      }
    }

    setAddButtonState(variant.available, variant.price * qty);

    // Broadcast on the pub/sub bus so any listener (cart drawer, badges, etc.) can react.
    if (
      typeof publish === "function" &&
      typeof PUB_SUB_EVENTS !== "undefined"
    ) {
      publish(PUB_SUB_EVENTS.variantChange, { variant: variant });
    }
  }

  // ---------- COLOR SWATCHES ----------
  const colorName = document.getElementById("colorName");
  document.querySelectorAll(".color-swatch").forEach((swatch) => {
    swatch.addEventListener("click", () => {
      activateInGroup(swatch, ".color-swatch");
      if (colorName && swatch.dataset.color)
        colorName.textContent = swatch.dataset.color;
      syncVariant();
    });
  });

  // ---------- SIZE SELECTOR ----------
  const sizeBtns = document.querySelectorAll(".size-btn");
  sizeBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      activateInGroup(btn, ".size-btn");
      syncVariant();
    });
  });

  // ---------- OTHER OPTION SELECTS ----------
  document.querySelectorAll("select.product-select").forEach((sel) => {
    sel.addEventListener("change", syncVariant);
  });

  // ---------- FIT PREFERENCE ----------
  const fitBtns = document.querySelectorAll(".fit-btn");

  fitBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      fitBtns.forEach((b) => b.classList.remove("active"));
      btn.classList.add("active");
    });
  });

  // ---------- FIND MY SIZE TOGGLE ----------
  const findSizeToggle = document.getElementById("findSizeToggle");
  const findSizeBody = document.getElementById("findSizeBody");
  const findSizeArrow = document.getElementById("findSizeArrow");

  if (findSizeToggle && findSizeBody) {
    findSizeToggle.addEventListener("click", () => {
      const isOpen = findSizeBody.classList.contains("open");
      findSizeBody.classList.toggle("open");
      findSizeArrow.textContent = isOpen ? "+" : "−";
    });
  }

  // ---------- FIND MY SIZE LOGIC ----------
  const findSizeSubmit = document.getElementById("findSizeSubmit");
  const findSizeResult = document.getElementById("findSizeResult");
  const recommendedSize = document.getElementById("recommendedSize");

  if (findSizeSubmit) {
    findSizeSubmit.addEventListener("click", () => {
      const height =
        parseInt(document.getElementById("heightInput").value) || 175;
      const weight =
        parseInt(document.getElementById("weightInput").value) || 75;
      const fit =
        document.querySelector(".fit-btn.active")?.dataset.fit || "regular";

      // Simple size recommendation algorithm
      let bmi = weight / (height / 100) ** 2;
      let size = "M";

      if (fit === "slim") {
        if (bmi < 20) size = "XS";
        else if (bmi < 22) size = "S";
        else if (bmi < 25) size = "M";
        else if (bmi < 28) size = "L";
        else if (bmi < 31) size = "XL";
        else if (bmi < 34) size = "XXL";
        else size = "3XL";
      } else if (fit === "relaxed") {
        if (bmi < 18) size = "S";
        else if (bmi < 21) size = "M";
        else if (bmi < 24) size = "L";
        else if (bmi < 27) size = "XL";
        else if (bmi < 30) size = "XXL";
        else size = "3XL";
      } else {
        if (bmi < 19) size = "XS";
        else if (bmi < 21.5) size = "S";
        else if (bmi < 24.5) size = "M";
        else if (bmi < 27.5) size = "L";
        else if (bmi < 30.5) size = "XL";
        else if (bmi < 33) size = "XXL";
        else size = "3XL";
      }

      // Adjust for height
      if (height > 185 && size !== "3XL") {
        const sizes = ["XS", "S", "M", "L", "XL", "XXL", "3XL"];
        const idx = sizes.indexOf(size);
        if (idx < sizes.length - 1) size = sizes[idx + 1];
      }

      recommendedSize.textContent = size;
      findSizeResult.style.display = "block";

      // Also select that size (match the real variant option value, or the placeholder data-size)
      sizeBtns.forEach((b) => {
        b.classList.remove("active");
        if (b.hasAttribute("aria-checked"))
          b.setAttribute("aria-checked", "false");
        if ((b.dataset.optionValue || b.dataset.size) === size) {
          b.classList.add("active");
          if (b.hasAttribute("aria-checked"))
            b.setAttribute("aria-checked", "true");
        }
      });
      syncVariant();
    });
  }

  // ---------- QUANTITY SELECTOR ----------
  const qtyMinus = document.getElementById("qtyMinus");
  const qtyPlus = document.getElementById("qtyPlus");
  const qtyValue = document.getElementById("qtyValue");

  // qtyValue is a real <input name="quantity">, so update .value (it is submitted with the form).
  function setQuantity(n) {
    n = parseInt(n, 10);
    if (isNaN(n) || n < 1) n = 1;
    if (n > 10) n = 10;
    if (qtyValue) qtyValue.value = n;
    syncVariant();
    return n;
  }

  if (qtyMinus)
    qtyMinus.addEventListener("click", () => setQuantity(getQuantity() - 1));
  if (qtyPlus)
    qtyPlus.addEventListener("click", () => setQuantity(getQuantity() + 1));
  if (qtyValue)
    qtyValue.addEventListener("change", () => setQuantity(qtyValue.value));

  // ---------- ADD TO CART ----------
  // The add itself is handled globally by maggd-drawers.js: it intercepts the
  // product form submit, adds via the Cart AJAX API, refreshes + opens the cart
  // drawer, and publishes the cart-update pub/sub event. We only guarantee the
  // correct variant id, price and quantity are in the form before it submits.
  syncVariant();

  // ==========================================
  //  VIRTUAL TRY-ON
  // ==========================================
  const tryOnBtn = document.getElementById("tryOnBtn");
  const tryOnModal = document.getElementById("tryOnModal");
  const tryOnClose = document.getElementById("tryOnClose");
  const tryOnOverlay = document.getElementById("tryOnOverlay");
  const tryOnVideo = document.getElementById("tryOnVideo");
  const tryOnJersey = document.getElementById("tryOnJersey");
  const tryOnJerseyImg = document.getElementById("tryOnJerseyImg");
  const tryOnResize = document.getElementById("tryOnResize");
  const tryOnCapture = document.getElementById("tryOnCapture");
  const tryOnCanvas = document.getElementById("tryOnCanvas");
  const tryOnPhoto = document.getElementById("tryOnPhoto");
  const tryOnActions = document.getElementById("tryOnActions");
  const tryOnRetake = document.getElementById("tryOnRetake");
  const tryOnSave = document.getElementById("tryOnSave");
  const tryOnFlip = document.getElementById("tryOnFlip");
  const tryOnReset = document.getElementById("tryOnReset");
  const tryOnPermission = document.getElementById("tryOnPermission");
  const tryOnInstructions = document.getElementById("tryOnInstructions");
  const tryOnViewport = document.getElementById("tryOnViewport");
  const tryOnControls = document.querySelector(".tryon-controls");

  if (tryOnBtn && tryOnModal) {
    let stream = null;
    let facingMode = "user"; // 'user' = front, 'environment' = rear
    let jerseyX = 0,
      jerseyY = 0;
    let jerseyW = 200;
    let isDraggingJersey = false;
    let isResizing = false;
    let dragStartX = 0,
      dragStartY = 0;
    let dragOffsetX = 0,
      dragOffsetY = 0;
    let resizeStartX = 0,
      resizeStartW = 0;
    let instructionsHidden = false;
    let isCaptured = false;

    // --- Open modal ---
    tryOnBtn.addEventListener("click", () => {
      tryOnModal.classList.add("active");
      document.body.style.overflow = "hidden";
      // Update jersey image to currently shown product image
      const currentImg = document.getElementById("mainImage");
      if (currentImg) tryOnJerseyImg.src = currentImg.src;
      startCamera();
    });

    // --- Close modal ---
    function closeModal() {
      tryOnModal.classList.remove("active");
      document.body.style.overflow = "";
      stopCamera();
      resetTryOn();
    }

    tryOnClose.addEventListener("click", closeModal);
    tryOnOverlay.addEventListener("click", closeModal);

    // Escape key closes
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && tryOnModal.classList.contains("active"))
        closeModal();
    });

    // --- Start camera ---
    async function startCamera() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: facingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        tryOnVideo.srcObject = stream;
        tryOnVideo.classList.toggle("rear", facingMode === "environment");
        tryOnPermission.classList.add("hidden");
      } catch (err) {
        console.warn("Camera access denied:", err);
        tryOnPermission.classList.remove("hidden");
      }
    }

    // --- Stop camera ---
    function stopCamera() {
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
        stream = null;
        tryOnVideo.srcObject = null;
      }
    }

    // --- Flip camera ---
    tryOnFlip.addEventListener("click", () => {
      facingMode = facingMode === "user" ? "environment" : "user";
      stopCamera();
      startCamera();
    });

    // --- Reset jersey position ---
    function resetJerseyPosition() {
      jerseyW = 200;
      jerseyX = 0;
      jerseyY = 0;
      tryOnJersey.style.width = jerseyW + "px";
      tryOnJersey.style.left = "50%";
      tryOnJersey.style.top = "50%";
      tryOnJersey.style.transform = "translate(-50%, -50%)";
    }

    tryOnReset.addEventListener("click", resetJerseyPosition);

    // --- Jersey drag (mouse) ---
    tryOnJersey.addEventListener("mousedown", (e) => {
      if (e.target === tryOnResize) return;
      isDraggingJersey = true;
      const rect = tryOnJersey.getBoundingClientRect();
      dragOffsetX = e.clientX - rect.left;
      dragOffsetY = e.clientY - rect.top;
      hideInstructions();
      e.preventDefault();
    });

    window.addEventListener("mousemove", (e) => {
      if (isDraggingJersey) {
        const vpRect = tryOnViewport.getBoundingClientRect();
        const x = e.clientX - vpRect.left - dragOffsetX;
        const y = e.clientY - vpRect.top - dragOffsetY;
        tryOnJersey.style.left = x + "px";
        tryOnJersey.style.top = y + "px";
        tryOnJersey.style.transform = "none";
      }
      if (isResizing) {
        const delta = e.clientX - resizeStartX;
        jerseyW = Math.max(80, Math.min(500, resizeStartW + delta));
        tryOnJersey.style.width = jerseyW + "px";
      }
    });

    window.addEventListener("mouseup", () => {
      isDraggingJersey = false;
      isResizing = false;
    });

    // --- Jersey drag (touch) ---
    tryOnJersey.addEventListener(
      "touchstart",
      (e) => {
        if (e.target === tryOnResize) return;
        if (e.touches.length === 1) {
          isDraggingJersey = true;
          const rect = tryOnJersey.getBoundingClientRect();
          dragOffsetX = e.touches[0].clientX - rect.left;
          dragOffsetY = e.touches[0].clientY - rect.top;
          hideInstructions();
        }
      },
      { passive: false },
    );

    tryOnJersey.addEventListener(
      "touchmove",
      (e) => {
        e.preventDefault();
        if (isDraggingJersey && e.touches.length === 1) {
          const vpRect = tryOnViewport.getBoundingClientRect();
          const x = e.touches[0].clientX - vpRect.left - dragOffsetX;
          const y = e.touches[0].clientY - vpRect.top - dragOffsetY;
          tryOnJersey.style.left = x + "px";
          tryOnJersey.style.top = y + "px";
          tryOnJersey.style.transform = "none";
        }
      },
      { passive: false },
    );

    tryOnJersey.addEventListener("touchend", () => {
      isDraggingJersey = false;
    });

    // --- Resize handle (mouse) ---
    tryOnResize.addEventListener("mousedown", (e) => {
      isResizing = true;
      resizeStartX = e.clientX;
      resizeStartW = jerseyW;
      e.stopPropagation();
      e.preventDefault();
    });

    // --- Resize handle (touch) ---
    tryOnResize.addEventListener(
      "touchstart",
      (e) => {
        isResizing = true;
        resizeStartX = e.touches[0].clientX;
        resizeStartW = jerseyW;
        e.stopPropagation();
      },
      { passive: false },
    );

    tryOnResize.addEventListener(
      "touchmove",
      (e) => {
        e.preventDefault();
        if (isResizing) {
          const delta = e.touches[0].clientX - resizeStartX;
          jerseyW = Math.max(80, Math.min(500, resizeStartW + delta));
          tryOnJersey.style.width = jerseyW + "px";
        }
      },
      { passive: false },
    );

    tryOnResize.addEventListener("touchend", () => {
      isResizing = false;
    });

    // --- Hide instructions ---
    function hideInstructions() {
      if (!instructionsHidden && tryOnInstructions) {
        instructionsHidden = true;
        tryOnInstructions.classList.add("hidden");
      }
    }

    // --- Capture photo ---
    tryOnCapture.addEventListener("click", () => {
      if (isCaptured) return;
      isCaptured = true;

      // Flash effect
      const flash = document.createElement("div");
      flash.className = "tryon-flash";
      tryOnViewport.appendChild(flash);
      setTimeout(() => flash.remove(), 500);

      // Draw video + jersey to canvas
      const vpRect = tryOnViewport.getBoundingClientRect();
      tryOnCanvas.width = vpRect.width * 2; // 2x for retina
      tryOnCanvas.height = vpRect.height * 2;
      const ctx = tryOnCanvas.getContext("2d");
      ctx.scale(2, 2);

      // Draw mirrored/normal video
      ctx.save();
      if (facingMode === "user") {
        ctx.translate(vpRect.width, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(tryOnVideo, 0, 0, vpRect.width, vpRect.height);
      ctx.restore();

      // Draw jersey overlay
      const jerseyRect = tryOnJersey.getBoundingClientRect();
      const jx = jerseyRect.left - vpRect.left;
      const jy = jerseyRect.top - vpRect.top;
      ctx.globalAlpha = 0.88;
      ctx.drawImage(
        tryOnJerseyImg,
        jx,
        jy,
        jerseyRect.width,
        jerseyRect.height,
      );
      ctx.globalAlpha = 1;

      // Show captured photo
      tryOnPhoto.src = tryOnCanvas.toDataURL("image/jpeg", 0.92);
      tryOnPhoto.style.display = "block";
      tryOnJersey.style.display = "none";
      tryOnControls.style.display = "none";
      tryOnActions.style.display = "flex";
    });

    // --- Retake ---
    tryOnRetake.addEventListener("click", () => {
      isCaptured = false;
      tryOnPhoto.style.display = "none";
      tryOnJersey.style.display = "block";
      tryOnControls.style.display = "flex";
      tryOnActions.style.display = "none";
    });

    // --- Save photo ---
    tryOnSave.addEventListener("click", () => {
      const link = document.createElement("a");
      link.download = "MAGGD-try-on.jpg";
      link.href = tryOnCanvas.toDataURL("image/jpeg", 0.92);
      link.click();
    });

    // --- Reset try-on state ---
    function resetTryOn() {
      isCaptured = false;
      tryOnPhoto.style.display = "none";
      tryOnJersey.style.display = "block";
      tryOnControls.style.display = "flex";
      tryOnActions.style.display = "none";
      instructionsHidden = false;
      tryOnInstructions.classList.remove("hidden");
      resetJerseyPosition();
    }

    // Auto-hide instructions after 4 seconds
    tryOnBtn.addEventListener("click", () => {
      setTimeout(() => {
        if (!instructionsHidden) hideInstructions();
      }, 4000);
    });
  }
});
